import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
} from '@mui/material';
import CommonButton from '../components/atom/CommonButton';
import { useTranslation } from 'react-i18next';
import { changeTransactionPaymentType } from '@openapi/transactions/transactions';
import useNotify from '../hooks/useNotify';

// Only cash / on-house methods with an identical processing fee may be swapped.
const TransactionPaymentTypeModal = ({
    transaction,
    paymentMethods,
    onSuccess,
    showModal,
    MODAL_TYPE,
}) => {
    const { t } = useTranslation();
    const { notifySuccess, notifyError } = useNotify();
    const [paymentType, setPaymentType] = useState('');
    const [loading, setLoading] = useState(false);

    const closeAction = () => showModal(MODAL_TYPE.NONE);

    const methods = paymentMethods || [];
    const currentMethod = methods.find((pm) => pm.slug === transaction?.paymentType);
    const eligible = methods.filter(
        (pm) =>
            pm.methodType === 'cash' &&
            pm.slug !== transaction?.paymentType &&
            currentMethod &&
            Number(pm.percentage) === Number(currentMethod.percentage) &&
            Number(pm.usd) === Number(currentMethod.usd),
    );

    const handleSave = async () => {
        if (!paymentType) return;
        try {
            setLoading(true);
            await changeTransactionPaymentType(transaction.id, { paymentType });
            notifySuccess(t('transactions.changePaymentType.success'));
            if (onSuccess) onSuccess();
            closeAction();
        } catch (err) {
            notifyError(err?.response?.data?.message || t('errors.unknown'));
        } finally {
            setLoading(false);
        }
    };

    if (!transaction) {
        return null;
    }

    return (
        <Dialog open onClose={closeAction} maxWidth="xs" fullWidth>
            <DialogTitle>{t('transactions.changePaymentType.title')}</DialogTitle>
            <DialogContent>
                <DialogContentText sx={{ mb: 2 }}>
                    {t('transactions.changePaymentType.help')}
                </DialogContentText>
                <FormControl fullWidth margin="dense" disabled={eligible.length === 0}>
                    <InputLabel>{t('transactions.changePaymentType.label')}</InputLabel>
                    <Select
                        value={paymentType}
                        label={t('transactions.changePaymentType.label')}
                        onChange={(e) => setPaymentType(e.target.value)}
                    >
                        {eligible.map((pm) => (
                            <MenuItem key={pm.slug} value={pm.slug}>
                                {pm.name}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </DialogContent>
            <DialogActions>
                <CommonButton
                    buttonType="secondary"
                    onClick={closeAction}
                    disabled={loading}
                    label={t('common.cancel')}
                />
                <CommonButton
                    buttonType="primary"
                    onClick={handleSave}
                    disabled={loading || !paymentType}
                    label={t('common.save')}
                />
            </DialogActions>
        </Dialog>
    );
};

export default TransactionPaymentTypeModal;
